import { useQuery } from '@tanstack/react-query';

import { OtaExecutionStatus, OtaStepStatus } from '../types';
import useImperativeRequestWrapper from '@/hooks/useImperativeRequestWrapper';
import { ExtractFnReturnType, QueryConfig } from '@/lib/react-query';

export interface IOtaStepProgress {
  step: string;
  status: OtaStepStatus;
  started_at?: string;
  duration_seconds?: number;
  error_code?: number;
  error_type?: string;
  failed?: boolean;
  job_id?: string;
}

export interface IOtaHistoricalExecution {
  id: string;
  lp_ui: string;
  status: OtaExecutionStatus;
  firmware_version: string;
  last_job_id: string;
  restart_count: number;
  last_error_code?: number;
  last_error_type: string;
  started_at: string;
  duration_seconds: number;
  steps: IOtaStepProgress[];
}

export interface IListRequest {
  page_size?: number;
  page_offset?: number;
  order_by?: string;
  filter?: string;
}

interface IGetOtaExecutionHistoryResponse {
  executions: IOtaHistoricalExecution[];
  total_size: number;
}

const fetchOtaExecutionHistory = async ({
  makeRequest,
  lpUI,
  listRequest,
}: {
  makeRequest: ReturnType<typeof useImperativeRequestWrapper>[1];
  lpUI: string;
  listRequest?: IListRequest;
}): Promise<IGetOtaExecutionHistoryResponse> => {
  const params = new URLSearchParams();

  if (listRequest?.page_size !== undefined) {
    params.set('page_size', String(listRequest.page_size));
  }

  if (listRequest?.page_offset !== undefined) {
    params.set('page_offset', String(listRequest.page_offset));
  }

  if (listRequest?.order_by) {
    params.set('order_by', listRequest.order_by);
  }

  if (listRequest?.filter) {
    params.set('filter', listRequest.filter);
  }

  const query = params.toString();
  const path = `/products/${lpUI}/ota/history${query ? `?${query}` : ''}`;

  const { data, error } = await makeRequest({ path, method: 'get' });

  if (!data || error) {
    throw new Error(error?.response?.data?.message ?? error);
  }

  return data;
};

type QueryFnType = typeof fetchOtaExecutionHistory;

type GetOtaExecutionHistoryOptions = {
  lpUI: string;
  listRequest?: IListRequest;
  config?: QueryConfig<QueryFnType>;
};

// Reason: Bulletproof React convention — query hooks are named getXxx (no use prefix).
// eslint-disable-next-line react-hooks/rules-of-hooks
export const getOtaExecutionHistory = ({
  lpUI,
  listRequest,
  config,
}: GetOtaExecutionHistoryOptions) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const [, makeRequest] = useImperativeRequestWrapper('deviceV3');

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return useQuery<ExtractFnReturnType<QueryFnType>>({
    ...config,
    queryKey: ['ota', 'history', lpUI, listRequest],
    queryFn: () => fetchOtaExecutionHistory({ makeRequest, lpUI, listRequest }),
  });
};
